import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ConsentTypes } from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics, TFormSubmissionStatus } from 'adobe-analytics';
import {
  cacChubSSNId,
  ConsentTextInfoQueryService,
  DealerLocation,
  monthlyPayment,
  portalApplicationId,
  ReSubmitLongFormApplicationMutationService,
  SubmitLongFormApplicationMutationService,
} from 'graphql-client';
import { catchError, map, takeUntil, tap, zip } from 'rxjs';

import { SSNError, SSNErrorType } from '../interfaces/common';
import { AWSService } from '../services/aws.service';
import { PrequalService } from '../services/prequal.service';
import { RuntimeConfigService } from '../services/runtime-config';
import { SkipRecaptchaService } from '../services/skip-recaptcha.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FLOW_TYPE, FS_EVENTS, FS_LABELS } from '../shared/constants/fsevents';
import {
  SiblingValidationService,
  ValidationResult,
} from '../validators/sibling-validation.service';
import { componentMapping, payloadCompiler } from './helpers';

declare const FS: typeof FullStory;
declare const grecaptcha: any;

@Component({
  selector: 'capq-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent extends Unsubscribe implements OnInit {
  @Output() activeStep = new EventEmitter<number>();
  @Output() setConsent = new EventEmitter<boolean>();
  @Output() resetIdentificationConsent = new EventEmitter<void>();
  @Output() ssnError: EventEmitter<SSNError> = new EventEmitter<SSNError>();

  @Output() submissionFailureReason: EventEmitter<string> =
    new EventEmitter<string>();
  @Input() adobeScriptLoaded = false;
  @Input() allConsentsGiven!: boolean;

  emailDisclosure = ConsentTypes.OSM_EMAIL_PHONE;
  NO_ACCOUNT = 'No Account';
  globalText: any;

  summaryConsentAgreed = new FormControl(false);
  personal_labels: any;
  personalData: any;
  residence_labels: any;
  residenceData: any;
  income_labels: any;
  primaryIncomeData: any;
  secondaryIncomeData: any;
  banking_labels: any;
  bankingData: string[] | undefined;
  identification_labels: any;
  steps: any;
  emailPhoneConsent = '';
  emailPhoneConsentVersion = '';
  secondaryIncomeDisplay = '';
  primaryIncomeDisplay = '';
  incomeOptionsList: any;
  emailConsentTextId = 0;
  dealerLocators: DealerLocation[] | undefined;
  openLoanOrNoBureauError: boolean | undefined;
  errorMessage: any | undefined;
  errorType: string | undefined;
  isInvalidSsn: boolean | undefined;
  summary: any;
  failureReason = '';
  isWaiting: boolean = false;
  private sortedResults: ValidationResult[] = [];
  private alive = true;

  componentsOrder = {
    1: '#personal-content',
    2: '#residence-content',
  };

  challengeSolved = inject(SkipRecaptchaService).skipRecaptcha();

  constructor(
    private awsService: AWSService,
    private readonly ConsentTextInfoQueryService: ConsentTextInfoQueryService,
    private readonly runtimeConfigService: RuntimeConfigService,
    private readonly siblingValidationService: SiblingValidationService,
    private readonly submitLongFormApplicationMutationService: SubmitLongFormApplicationMutationService,
    private readonly reSubmitLongFormApplicationMutationService: ReSubmitLongFormApplicationMutationService,
    private readonly prequalService: PrequalService,
    private readonly skipRecaptchaService: SkipRecaptchaService
  ) {
    super();
  }

  ngOnInit(): void {
    zip([
      this.awsService.prequalifiedJSONData$.pipe(
        tap((data) => {
          if (data) {
            this.globalText = data;
            this.summary = data.summary;
            this.personal_labels = data.personal;
            this.residence_labels = data.residence;
            this.income_labels = data.income;
            this.incomeOptionsList =
              data.income?.formsFields?.incomeType?.options;
            this.banking_labels = data.accounts;
            this.identification_labels = data.identification;
          }
        })
      ),
      this.ConsentTextInfoQueryService.fetch({
        consentType: ConsentTypes.OSM_EMAIL_PHONE,
      }).pipe(map((result) => result.data.getConsentByType)),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([_, emailDisclosureInfo]) => {
        if (emailDisclosureInfo) {
          this.emailPhoneConsent = emailDisclosureInfo.consenttext;
          this.emailPhoneConsentVersion = emailDisclosureInfo.consentversion;
          this.emailConsentTextId = emailDisclosureInfo.consenttextid;
        }
      });
    this.summaryConsentAgreed.addValidators(Validators.pattern('true'));
    this.summaryConsentAgreed.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.adobeScriptLoaded && adobeAnalytics.emitFormStart('agreement');
        this.setConsent.emit(value ?? false);
      });

    grecaptcha.ready(() => {
      grecaptcha.render('g-recaptcha-container', {
        sitekey:
          this.runtimeConfigService.prequalConfig?.RECAPTCHA_SITEKEY || '',
        callback: (token: string) => {
          this.challengeSolved = true;
          this.handleSubmit(this.sortedResults, token);
        },
      });
    });

    this.siblingValidationService.validationResult$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((results) => {
        const { validationPassed, sortedResults } =
          this.handleValidationResults(results);

        if (validationPassed) {
          if (this.challengeSolved) {
            this.handleSubmit(sortedResults);
          } else {
            this.sortedResults = sortedResults;
            grecaptcha.execute();
          }
        } else {
          this.isWaiting = false;
        }
      });
  }

  processFormSubmission() {
    this.isWaiting = true;
    this.siblingValidationService.triggerValidation();
  }

  handleSubmit(sortedResults: ValidationResult[], recaptchaToken = '') {
    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.PREQUALIFICATION, {
        action_label: FS_LABELS.PREQUALIFICATION,
        prequalificationSubmitted: true,
      });
    }
    if (portalApplicationId() > 0 && this.isInvalidSsn) {
      this.isInvalidSsn = false;
      const inputData: any = payloadCompiler(
        sortedResults,
        this.summaryConsentAgreed.value!
      );
      this.reSubmitLongFormApplicationMutationService
        .mutate(inputData, {
          context: {
            headers: this.skipRecaptchaService.skipRecaptchaDueToTestScript()
              ? {
                  'Is-Test-Script': 'true',
                }
              : {
                  recaptcha: recaptchaToken, // Set the recaptcha token as a header for submission to the backend.
                },
          },
        })
        .pipe(
          tap(({ data, errors }) => {
            this.isWaiting = false;
            let status = '';
            if (errors?.length) {
              status = 'failure';
            } else if (data) {
              if (data?.reSubmitLongFormApplication?.dealerLocations) {
                this.prequalService.setDealerLocations(
                  data.reSubmitLongFormApplication
                    .dealerLocations as DealerLocation[]
                );
              }
              status = data?.reSubmitLongFormApplication?.status;
              data?.reSubmitLongFormApplication?.portalApplicationId &&
                portalApplicationId(
                  data?.reSubmitLongFormApplication?.portalApplicationId
                );
              if (!(typeof FS === 'undefined')) {
                FS.setUserVars({
                  applicationId:
                    data?.reSubmitLongFormApplication?.portalApplicationId ?? 0,
                  FormType: FLOW_TYPE.OSM,
                });
              }

              if (status === 'success') {
                data?.reSubmitLongFormApplication?.maxMonthlyPayment &&
                  monthlyPayment(
                    data?.reSubmitLongFormApplication?.maxMonthlyPayment
                  );
                this.activeStep.emit(7);
              } else if (status === 'failure') {
                this.failureReason =
                  data.reSubmitLongFormApplication?.failureReason;
                if (data.reSubmitLongFormApplication?.invalidSsn) {
                  if (
                    data.reSubmitLongFormApplication?.failureReason.includes(
                      'blocked'
                    )
                  ) {
                    this.errorMessage = this.summary.errors.sme2;
                    this.errorType = 'blockedSSN';
                    this.ssnError.emit({
                      type: SSNErrorType.BLOCKED_SSN,
                      message: this.errorMessage,
                    });
                    this.clearAllInputFields();
                  } else {
                    this.errorMessage = this.summary.errors.sme1;
                    this.errorType = 'invalidSSN';
                    this.ssnError.emit({
                      type: SSNErrorType.INVALID_SSN,
                      message: this.errorMessage,
                    });
                    this.prequalService.setErrorMessage(true);
                    this.isInvalidSsn = true;
                  }
                  this.uncheckAgreementCheckboxes();
                  document.getElementById('error-subsection')?.scrollIntoView();
                } else if (data.reSubmitLongFormApplication?.openLoan) {
                  if (!(typeof FS === 'undefined')) {
                    FS.setUserVars({
                      action_label: FS_LABELS.OPEN_LOAN,
                      openLoanExist: true,
                    });
                  }

                  this.setSpecialErrorScenarioData();
                } else if (data.reSubmitLongFormApplication?.frozenBureau) {
                  if (data.reSubmitLongFormApplication?.failureReason) {
                    let bureauName =
                      data.reSubmitLongFormApplication?.failureReason;
                    bureauName = bureauName.split('Frozen Bureau - ')[1];
                    this.errorMessage = this.summary.errors.sme3
                      .split('[bureau name]')
                      .join(bureauName);
                  } else {
                    this.errorMessage = this.summary.errors.sme1;
                  }
                  this.uncheckAgreementCheckboxes();
                  document.getElementById('error-subsection')?.scrollIntoView();
                  this.errorType = 'frozenBureauExists';
                  this.ssnError.emit({
                    type: SSNErrorType.FrozenBUREAU,
                    message: this.errorMessage,
                  });
                } else {
                  this.prequalService.triggerErrorScreen(true);
                }
              } else {
                this.prequalService.triggerErrorScreen(true);
              }
            }

            if (!(typeof FS === 'undefined')) {
              FS.event(FS_EVENTS.PREQUALIFICATION, {
                action_label: FS_LABELS.PREQUALIFICATION,
                prequalificationSubmitted: true,
              });
            }

            this.adobeScriptLoaded &&
              adobeAnalytics.emitEvent('cac-form-complete', null, {
                form_design: 'v 1.0',
                section_name: 'summary',
                form_submission_id: (portalApplicationId() || '').toString(),
                dealer_list: '',
                form_submission_status: status as TFormSubmissionStatus,
                event_type: 'cac-form-complete',
                max_finance_amount:
                  data?.reSubmitLongFormApplication?.maxFinanceAmount!,
                max_monthly_payment_amount:
                  data?.reSubmitLongFormApplication?.maxMonthlyPayment!,
                min_down_payment_amount:
                  data?.reSubmitLongFormApplication?.minimumDownPayment!,
                test_experience: 'form prequal monthlypayment challenger1',
                test_name: 'form prequal monthlypayment ',
                test_response: 'monthly_payment',
                form_failure_reason: this.failureReason,
                email_address: inputData?.longFormInfoInput?.personal?.email,
                phone_number: inputData.longFormInfoInput?.personal.phone,
                user_zip: inputData.longFormInfoInput?.residence?.zipCode,
                user_city: inputData.longFormInfoInput?.residence?.city,
                user_state: inputData.longFormInfoInput?.residence?.userState,
                consumer_uuid: cacChubSSNId(),
              });
          }),
          catchError((context) => {
            if (!(typeof FS === 'undefined')) {
              FS.setUserVars({
                applicationId:
                  context.graphQLErrors[0]?.extensions?.applicationId ?? 0,
                FormType: FLOW_TYPE.OSM,
              });
            }
            this.prequalService.triggerErrorScreen(true);
            throw new Error();
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    } else {
      const inputData: any = payloadCompiler(
        sortedResults,
        this.summaryConsentAgreed.value!
      );
      this.submitLongFormApplicationMutationService
        .mutate(inputData, {
          context: {
            headers: this.skipRecaptchaService.skipRecaptchaDueToTestScript()
              ? {
                  'Is-Test-Script': 'true',
                }
              : {
                  recaptcha: recaptchaToken, // Set the recaptcha token as a header for submission to the backend.
                },
          },
        })
        .pipe(
          tap(({ data, errors }) => {
            this.isWaiting = false;
            let status = '';
            if (errors?.length) {
              status = 'failure';
            } else if (data) {
              if (data?.submitLongFormApplication?.dealerLocations) {
                this.prequalService.setDealerLocations(
                  data.submitLongFormApplication
                    .dealerLocations as DealerLocation[]
                );
              }
              status = data?.submitLongFormApplication?.status;
              data?.submitLongFormApplication?.portalApplicationId &&
                portalApplicationId(
                  data?.submitLongFormApplication?.portalApplicationId
                );
              if (!(typeof FS === 'undefined')) {
                FS.setUserVars({
                  applicationId:
                    data?.submitLongFormApplication?.portalApplicationId ?? 0,
                  FormType: FLOW_TYPE.OSM,
                });
              }

              if (status === 'success') {
                data?.submitLongFormApplication?.maxMonthlyPayment &&
                  monthlyPayment(
                    data?.submitLongFormApplication?.maxMonthlyPayment
                  );
                this.activeStep.emit(7);
              } else if (status === 'failure') {
                this.failureReason =
                  data.submitLongFormApplication?.failureReason;
                if (data.submitLongFormApplication?.invalidSsn) {
                  if (
                    data.submitLongFormApplication?.failureReason.includes(
                      'blocked'
                    )
                  ) {
                    this.errorMessage = this.summary.errors.sme2;
                    this.errorType = 'blockedSSN';
                    this.ssnError.emit({
                      type: SSNErrorType.BLOCKED_SSN,
                      message: this.errorMessage,
                    });
                    this.clearAllInputFields();
                  } else {
                    this.errorMessage = this.summary.errors.sme1;
                    this.errorType = 'invalidSSN';
                    this.ssnError.emit({
                      type: SSNErrorType.INVALID_SSN,
                      message: this.errorMessage,
                    });
                    this.prequalService.setErrorMessage(true);
                    this.isInvalidSsn = true;
                  }
                  this.uncheckAgreementCheckboxes();
                  document.getElementById('error-subsection')?.scrollIntoView();
                } else if (data.submitLongFormApplication?.openLoan) {
                  if (!(typeof FS === 'undefined')) {
                    FS.setUserVars({
                      action_label: FS_LABELS.OPEN_LOAN,
                      openLoanExist: true,
                    });
                  }

                  this.setSpecialErrorScenarioData();
                } else if (data.submitLongFormApplication?.frozenBureau) {
                  if (data.submitLongFormApplication?.failureReason) {
                    let bureauName =
                      data.submitLongFormApplication?.failureReason;
                    bureauName = bureauName.split('Frozen Bureau - ')[1];
                    this.errorMessage = this.summary.errors.sme3
                      .split('[bureau name]')
                      .join(bureauName);
                  } else {
                    this.errorMessage = this.summary.errors.sme1;
                  }
                  this.uncheckAgreementCheckboxes();
                  document.getElementById('error-subsection')?.scrollIntoView();
                  this.errorType = 'frozenBureauExists';
                  this.ssnError.emit({
                    type: SSNErrorType.FrozenBUREAU,
                    message: this.errorMessage,
                  });
                } else {
                  this.prequalService.triggerErrorScreen(true);
                }
              } else {
                this.prequalService.triggerErrorScreen(true);
              }
            }

            if (!(typeof FS === 'undefined')) {
              FS.event(FS_EVENTS.PREQUALIFICATION, {
                action_label: FS_LABELS.PREQUALIFICATION,
                prequalificationSubmitted: true,
              });
            }

            this.adobeScriptLoaded &&
              adobeAnalytics.emitEvent('cac-form-complete', null, {
                form_design: 'v 1.0',
                section_name: 'summary',
                form_submission_id: (portalApplicationId() || '').toString(),
                dealer_list: '',
                form_submission_status: status as TFormSubmissionStatus,
                event_type: 'cac-form-complete',
                max_finance_amount:
                  data?.submitLongFormApplication?.maxFinanceAmount!,
                max_monthly_payment_amount:
                  data?.submitLongFormApplication?.maxMonthlyPayment!,
                min_down_payment_amount:
                  data?.submitLongFormApplication?.minimumDownPayment!,
                test_experience: 'form prequal monthlypayment challenger1',
                test_name: 'form prequal monthlypayment ',
                test_response: 'monthly_payment',
                form_failure_reason: this.failureReason,
                email_address: inputData?.longFormInfoInput?.personal?.email,
                phone_number: inputData.longFormInfoInput?.personal.phone,
                user_zip: inputData.longFormInfoInput?.residence?.zipCode,
                user_city: inputData.longFormInfoInput?.residence?.city,
                user_state: inputData.longFormInfoInput?.residence?.userState,
                consumer_uuid: cacChubSSNId(),
              });
          }),
          catchError((context) => {
            if (!(typeof FS === 'undefined')) {
              FS.setUserVars({
                applicationId:
                  context.graphQLErrors[0]?.extensions?.applicationId ?? 0,
                FormType: FLOW_TYPE.OSM,
              });
            }
            this.prequalService.triggerErrorScreen(true);
            throw new Error();
          }),
          takeUntil(this.unsubscribe$)
        )
        .subscribe();
    }
  }

  handleValidationResults(results: ValidationResult[]): {
    validationPassed: boolean;
    sortedResults: ValidationResult[];
  } {
    let validationPassed = true;
    const sortedResults = [...results].sort(
      (a: ValidationResult, b: ValidationResult) =>
        a.componentId - b.componentId
    );
    const firstInvalidComponent = sortedResults.find(
      (result: any) => !result.isValid
    );
    if (firstInvalidComponent) {
      validationPassed = false;
      const id =
        componentMapping[
          firstInvalidComponent.componentId as keyof typeof componentMapping
        ];
      if (firstInvalidComponent && id) {
        document
          .querySelector(id)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    return {
      validationPassed,
      sortedResults,
    };
  }

  setSpecialErrorScenarioData() {
    this.openLoanOrNoBureauError = true;
    this.activeStep.emit(8);
  }

  clearAllInputFields() {
    (document.getElementById('cac-summary-submit') as any).disabled = true;

    document.querySelectorAll('input').forEach((input) => {
      input.value = '';
    });

    document.querySelectorAll('input[type=checkbox]').forEach((input) => {
      (input as any).disabled = true;
      (input as any).checked = false;
    });
  }

  uncheckAgreementCheckboxes() {
    this.summaryConsentAgreed.setValue(false);
    this.resetIdentificationConsent.emit();
  }
}
